<script setup>
import { useObjectsStore } from '@/store/objects'
import { computed, ref, watch, watchEffect, inject } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { FilterMatchMode } from '@primevue/core/api'
import { useConfirm } from 'primevue/useconfirm'
import { useDialog } from 'primevue/usedialog'
import { useDark } from '@vueuse/core'
import MappingConfirmationModal from './modals/MappingConfirmationModal.vue'

function htmlUnescape (str) {
  const htmlEntities = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#39;': "'",
  }

  return str.replace(/&(?:amp|lt|gt|quot|#39);/g, match => htmlEntities[match])
}

const confirm = useConfirm()
const isDark = useDark()
const dialog = useDialog()
const windowTooSmall = inject('windowTooSmall')

const objectStore = useObjectsStore()

const route = useRoute()
const router = useRouter()
const loading = ref(true)
const post = ref(null)
const error = ref(null)

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms))
watch(() => route.params.objectName, fetchData, { immediate: true })

async function fetchData (objectName) {
  error.value = post.value = null
  loading.value = true
  try {
    await sleep(0)
    const [_currentObject, _metadata] = await Promise.all([
      objectStore.fetchCurrentObject(objectName, true, true),
      objectStore.fetchObjectMetadata(objectName)
    ])
    currentObject.value = _currentObject || objectStore.getDefaultNewSObjectMapping(route.params.objectName)
    metadata.value = _metadata
  } catch (err) {
    error.value = err.toString()
  } finally {
    loading.value = false
  }
}

const metadata = ref()
const currentObject = ref()
const savedFieldsSet = computed(() => new Set(currentObject.value?.fields || []))
const savedIndexesSet = computed(() => new Set(currentObject.value?.indexes || []))
const metadataFields = computed(() => Object.values(metadata.value?.fields || {}).map(f => ({ label: f.label, apiName: f.apiName, dataType: f.dataType, indexed: false, selected: false })))

// Keep track of changes
const fields = ref([])
const newFields = computed(() => fields.value.filter(f => f.selected && !f.originallySelected) || [])
const fieldsToRemove = computed(() => fields.value.filter(f => !f.selected && f.originallySelected) || [])
const newIndices = computed(() => fields.value.filter(f => f.indexed && !f.originallyIndexed) || [])
const indicesToRemove = computed(() => fields.value.filter(f => !f.indexed && f.originallyIndexed) || [])
const newFieldsMap = computed(() => new Map(newFields.value.map(el => [el.apiName, el])))
const fieldsToRemoveMap = computed(() => new Map(fieldsToRemove.value.map(el => [el.apiName, el])))
const newIndicesMap = computed(() => new Map(newIndices.value.map(el => [el.apiName, el])))
const indicesToRemoveMap = computed(() => new Map(indicesToRemove.value.map(el => [el.apiName, el])))

watchEffect(() => {
  fields.value = metadataFields.value.map(el => {
    return {
      ...el,
      _selected: savedFieldsSet.value.has(el.apiName) || ['Id', 'SystemModstamp', 'IsDeleted'].includes(el.apiName),
      indexed: savedIndexesSet.value.has(el.apiName) || ['Id', 'SystemModstamp'].includes(el.apiName) || false,
      get selected () {
        return this._selected
      },
      set selected (val) {
        this._selected = val
        if (!val) this.indexed = false
      },
      originallySelected: savedFieldsSet.value.has(el.apiName),
      originallyIndexed: savedIndexesSet.value.has(el.apiName),
      label: el.label ? htmlUnescape(el.label) : el.label
    }
  })
})

const filters = ref()
const initFilters = () => {
  filters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS }
  }
}

initFilters()

const openSaveChangesModal = () => {
  dialog.open(MappingConfirmationModal, {
    props: {
      header: 'Please review the changes',
      style: {
        width: windowTooSmall.value ? '90vw' : '800px',
        height: windowTooSmall.value ? '100vh' : '80vh'
      },
      contentStyle: 'overflow-y: hidden; display: flex; flex-direction: column; flex-grow: 1',
      modal: true
    },
    data: { newFields, fieldsToRemove, newIndices, indicesToRemove, currentObject }
  })
}

const confirmDelete = (objToDelete) => {
  confirm.require({
    group: 'confirm-mapping-deletion',
    header: `Delete ${objToDelete} Mapping`,
    message: objToDelete,
    icon: 'pi pi-exclamation-triangle',
    rejectLabel: 'Cancel',
    rejectProps: {
      label: 'Cancel',
      severity: 'secondary',
      outlined: true
    },
    acceptProps: {
      label: 'Delete',
      severity: 'danger'
    },
    accept: async () => {
      await deleteItem(objToDelete)
    }
  })
}

const deleteItem = async (objName) => {
  loading.value = true
  try {
    await objectStore.deleteObjectMappingFromDB(objName)
    loading.value = false
    router.push('/objects/')
  } catch (err) {
    // TODO: manage error
  }
}

const buttons = [{
  label: 'Delete Mapping',
  command: () => {
    confirmDelete(currentObject.value.objectName)
  }
}]

const save = () => {
  openSaveChangesModal()
}

const rowStyle = (data) => {
  const hasBeenAdded = newFieldsMap.value.get(data.apiName) && !['SystemModstamp', 'Id', 'IsDeleted'].includes(data.apiName)
  const hasBeenRemoved = fieldsToRemoveMap.value.get(data.apiName)
  return {
    'background-color': hasBeenAdded ? (isDark.value ? '#86efac' : '#f3fcf9') : hasBeenRemoved ? (isDark.value ? '#ff8780' : '#fff5f5') : undefined,
    color: isDark.value && (hasBeenAdded || hasBeenRemoved) ? '#000' : undefined
  }
}

const getSelectionClass = (data) => {
  if (newIndicesMap.value.get(data.apiName) && !['SystemModstamp', 'Id', 'IsDeleted'].includes(data.apiName)) return 'added-index'
  if (indicesToRemoveMap.value.get(data.apiName)) return 'removed-index'
  return null
}

const allFieldsSelected = ref(false)

const selectAll = (val) => {
  allFieldsSelected.value = val.checked
  fields.value.forEach(f => {
    if (!['SystemModstamp', 'Id', 'IsDeleted'].includes(f.apiName)) {
      f.selected = val.checked
    }
  })
}

</script>

<template>
  <ConfirmDialog group="confirm-mapping-deletion">
    <template #message="slotProps">
      <div class="flex flex-col items-center w-full gap-4 border-b border-surface-200 dark:border-surface-700">
        <i
          :class="slotProps.message.icon"
          class="!text-6xl text-primary-500"
        />
        <p>Are you sure you want to delete <b>{{ slotProps.message.message }}</b> mapping?</p>
        <p>This operation <b><u>CANNOT</u></b> be undone.</p>
        <br>
      </div>
    </template>
  </ConfirmDialog>
  <div class="card relative">
    <div :style="{position: 'absolute', left:'0px', top: '0px'}">
      <router-link
        to="/objects"
      >
        <Avatar
          icon="pi pi-angle-left"
          size="large"
          :style="{backgroundColor: '#6366F1', color: 'white', cursor: 'pointer',borderBottomLeftRadius: 0, borderTopRightRadius: 0,borderTopLeftRadius: windowTooSmall ? 0 : undefined }"
        />
      </router-link>
    </div>
    <div
      class="flex lg:flex-row lg:gap-0 gap-4 justify-between lg:items-center h-fit lg:h-12 pl-12"
    >
      <div
        class="flex gap-2 items-center w-full"
      >
        <SfIcon
          v-if="!windowTooSmall"
          :icon="currentObject?.iconName || ''"
          :color="currentObject?.iconColor || ''"
        />
        <IconField
          class="w-full"
          style="max-width: 204px;"
        >
          <InputIcon>
            <i class="pi pi-search" />
          </InputIcon>
          <InputText
            v-model="filters['global'].value"
            v-focus="!windowTooSmall"
            class="w-full"
            placeholder="Keyword Search"
          />
        </IconField>
      </div>
      <div class="flex gap-2 w-full justify-end">
        <template v-if="currentObject">
          <SplitButton
            v-if="!currentObject.isNewSetup"
            label="Save"
            :model="buttons"
            @click="save"
          />
          <template v-else>
            <ButtonGroup>
              <Button
                :label="windowTooSmall ? '': 'Cancel'"
                :icon="windowTooSmall ? 'pi pi-times' : null"
                severity="secondary"
                @click="router.push('/objects/')"
              />
              <Button
                :label="windowTooSmall ? 'Save' : 'Review and Save'"
                @click="save"
              />
            </ButtonGroup>
          </template>
        </template>
      </div>
    </div>
    <Divider v-if="!windowTooSmall" />
    <div
      v-if="loading"
      class="flex flex-1 w-full items-center justify-center"
    >
      <ProgressSpinner />
    </div>
    <div
      v-else
      class="flex-1 overflow-y-scroll min-h-0 -mx-4 -mb-4 md:mb-0 md:mx-0 md:px-10 pt-4 md:pb-4 rounded-lg bg-[--p-content-background]"
    >
      <DataTable
        v-model:filters="filters"
        :virtual-scroller-options="{ itemSize: 57.81, numToleratedItems: 25 }"
        scrollable
        scroll-height="flex"
        removable-sort
        :global-filter-fields="['apiName', 'label']"
        :value="fields"
        :row-style="rowStyle"
        :select-all="allFieldsSelected"
        @select-all-change="selectAll"
      >
        <template #empty>
          No mappings found
        </template>
        <Column
          v-if="!windowTooSmall"
          selection-mode="multiple"
          header="Sync"
          field="selected"
          header-style="width: 3rem"
        >
          <template #body="{ data }">
            <div class="flex w-full md:justify-end items-center">
              <Checkbox
                v-model="data.selected"
                :disabled="['SystemModstamp', 'Id', 'IsDeleted'].includes(data.apiName)"
                :binary="true"
              />
            </div>
          </template>
        </Column>
        <Column
          field="apiName"
          :selection-mode="windowTooSmall ? 'multiple' : null"
          sortable
          :style="{'max-width': windowTooSmall ? '50px' : null}"
        >
          <template #header>
            <SfIcon
              v-if="windowTooSmall"
              class="w-6 h-6"
              :icon="currentObject?.iconName || ''"
              :color="currentObject?.iconColor || ''"
            />
            <strong class="font-semibold">API Name</strong>
          </template>
          <template #body="{ data }">
            <div class="flex flex-row items-center gap-2">
              <Checkbox
                v-if="windowTooSmall"
                v-model="data.selected"
                :disabled="['SystemModstamp', 'Id', 'IsDeleted'].includes(data.apiName)"
                :binary="true"
              />
              <div class="w-full overflow-hidden whitespace-nowrap">
                <div
                  class="text-surface-600 overflow-hidden text-ellipsis"
                >
                  {{ data.label }}
                </div>
                <div class="text-sm text-surface-500 dark:text-surface-400 overflow-hidden text-ellipsis">
                  {{ data.apiName }}
                </div>
              </div>
            </div>
          </template>
        </Column>
        <Column
          v-if="!windowTooSmall"
          field="dataType"
          sortable
          header="Data Type"
          header-style="width: 150px"
        />
        <Column
          field="indexed"
          sortable
          :header="windowTooSmall ? 'Idx' : 'Indexed'"
          :header-style="{width: windowTooSmall ? '20px' : '150px'}"
        >
          <template #body="{ data }">
            <div
              class="flex justify-center items-center w-7 h-7"
              :class="getSelectionClass(data)"
            >
              <Checkbox
                v-model="data.indexed"
                :disabled="['SystemModstamp', 'Id', 'IsDeleted'].includes(data.apiName) || !data.selected"
                :binary="true"
              />
            </div>
          </template>
        </Column>
        <ColumnGroup
          v-if="!windowTooSmall"
          type="footer"
        >
          <Row>
            <Column footer="Totals:" />
            <Column
              :colspan="4"
              :footer="`${newFields.length} fields added, ${fieldsToRemove.length} fields removed, ${newIndices.length} indices added, ${indicesToRemove.length} indices removed`"
            />
          </Row>
        </ColumnGroup>
      </DataTable>
    </div>
  </div>
</template>

<style scoped>
* {
  --p-checkbox-transition-duration: 0s;
  --p-datatable-transition-duration: 0s;
}
.p-datatable-scrollable-table>.p-datatable-tfoot {
  bottom: -1px;
}
.added-index {
  background-color: #6bd4b1;
}
.removed-index {
  background-color: #ff8780;
}
</style>
